import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Call, Clock, Location, Sms, ArrowLeft2, Share } from "iconsax-react";
import styled from "styled-components";

import MobileLayout from "../../components/MobileLayout";
import { MapActionSheet } from "../../features/map/components/MapActionSheet";
import { PoiNearbyTag } from "../../features/map/components/PoiNearbyTag";
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  MAP_COPY,
} from "../../features/map/constants";
import { useMapUi } from "../../features/map/hooks/useMapUi";
import {
  getPoiDetails,
  isPoiNearbyToUser,
  recordPoiAccess,
} from "../../features/map/services/mapService";
import { getCurrentCoordinates } from "../../features/map/utils/geolocation";
import {
  getMapAppActions,
  getPhoneContactActions,
  type MapActionOption,
} from "../../features/map/utils/poiContactLinks";
import { CategoryIcon } from "../../features/map/utils/categoryIcons";
import { Md3Button } from "../../shared/components";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const HeroCard = styled.div<{ $tint: string }>`
  padding: 20px;
  border-radius: ${({ theme }) => theme.borderRadius.xl}px;
  background: color-mix(in srgb, ${({ $tint }) => $tint} 8%, transparent);
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const HeroRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const HeroText = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.25;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const HeroIcon = styled.div<{ $bg: string }>`
  width: 72px;
  height: 72px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  flex-shrink: 0;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const InfoText = styled.div`
  flex: 1;
  min-width: 0;
`;

const InfoLabel = styled.div`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 19px;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.4;
`;

const LinkText = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 19px;
  color: ${({ theme }) => theme.colors.primary};
  text-align: left;
  cursor: pointer;
  line-height: 1.4;
  text-decoration: underline;
`;

const DactylologyCard = styled.button`
  margin-top: 12px;
  width: 100%;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.lg}px;
  border: 1px dashed ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  cursor: pointer;
  text-align: left;
`;

const DactylologyLabel = styled.div`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const DactylologyAction = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.primary};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CategoryFooter = styled.div`
  text-align: center;
  padding-top: 8px;
`;

const CategoryFooterText = styled.span<{ $color: string }>`
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ $color }) => $color};
`;

type ActionSheetState = {
  open: boolean;
  title: string;
  description?: string;
  options: MapActionOption[];
};

const CLOSED_SHEET: ActionSheetState = {
  open: false,
  title: "",
  options: [],
};

function normalizeForPreview(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z]/g, "")
    .toUpperCase()
    .slice(0, 12);
}

const MapPoiDetailsPage: React.FC = () => {
  const { poiId = "" } = useParams<{ poiId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mapUi = useMapUi();

  const routeLat = searchParams.get("lat");
  const routeLng = searchParams.get("lng");
  const isNearbyHint = searchParams.get("nearby") === "1";

  const [userCoords, setUserCoords] = useState<{
    latitude: number;
    longitude: number;
  } | null>(() => {
    if (routeLat != null && routeLng != null) {
      return { latitude: Number(routeLat), longitude: Number(routeLng) };
    }
    return null;
  });

  const [actionSheet, setActionSheet] =
    useState<ActionSheetState>(CLOSED_SHEET);

  const poi = getPoiDetails(poiId);

  useEffect(() => {
    if (poiId) recordPoiAccess(poiId);
  }, [poiId]);

  useEffect(() => {
    (async () => {
      const coords = await getCurrentCoordinates({ timeoutMs: 12_000 });
      if (coords) setUserCoords(coords);
    })();
  }, []);

  const isNearby = useMemo(() => {
    if (isNearbyHint) return true;
    if (!poi) return false;
    return isPoiNearbyToUser(poi, userCoords?.latitude, userCoords?.longitude);
  }, [isNearbyHint, poi, userCoords?.latitude, userCoords?.longitude]);

  const closeActionSheet = useCallback(() => {
    setActionSheet(CLOSED_SHEET);
  }, []);

  const openMapAppsSheet = useCallback(() => {
    if (!poi) return;
    setActionSheet({
      open: true,
      title: MAP_COPY.openMapTitle,
      description: MAP_COPY.openMapDescription,
      options: getMapAppActions({
        title: poi.title,
        address: poi.address,
        latitude: poi.latitude,
        longitude: poi.longitude,
      }),
    });
  }, [poi]);

  const openPhoneSheet = useCallback(() => {
    if (!poi?.phone) return;
    setActionSheet({
      open: true,
      title: MAP_COPY.contactTitle,
      description: MAP_COPY.contactDescription,
      options: getPhoneContactActions({
        phone: poi.phone,
        placeName: poi.title,
      }),
    });
  }, [poi]);

  if (!poi) {
    return (
      <MobileLayout
        headerTitle={MAP_COPY.poiDetails}
        headerType="private"
        showBannerAd={false}
      >
        <p>Local não encontrado.</p>
        <Md3Button title="Voltar ao mapa" onClick={() => navigate("/map")} />
      </MobileLayout>
    );
  }

  const categoryColor = CATEGORY_COLORS[poi.category];
  const previewLetters = normalizeForPreview(poi.title);

  const handleShare = async () => {
    const lines = [
      poi.title,
      poi.subtitle,
      `${MAP_COPY.addressLabel}: ${poi.address}`,
      poi.phone ? `${MAP_COPY.phoneLabel}: ${poi.phone}` : null,
      poi.email ? `${MAP_COPY.emailLabel}: ${poi.email}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    if (navigator.share) {
      await navigator.share({ title: poi.title, text: lines });
    } else {
      await navigator.clipboard.writeText(lines);
    }
  };

  return (
    <MobileLayout
      headerTitle={MAP_COPY.poiDetails}
      headerType="private"
      showBannerAd={false}
    >
      <Content>
        <button
          type="button"
          onClick={() => navigate("/map")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "var(--color-primary)",
            padding: 0,
            fontWeight: 600,
          }}
        >
          <ArrowLeft2 size={18} />
          Voltar ao mapa
        </button>

        <HeroCard $tint={mapUi.primary}>
          <HeroRow>
            <HeroText>
              <Title>{poi.title}</Title>
              {isNearby ? <PoiNearbyTag accentColor={categoryColor} /> : null}
              {previewLetters.length > 0 ? (
                <DactylologyCard
                  type="button"
                  onClick={() =>
                    navigate("/dactylology", { state: { word: poi.title } })
                  }
                >
                  <DactylologyLabel>
                    {MAP_COPY.dactylologyLabel}
                  </DactylologyLabel>
                  <div style={{ letterSpacing: 4, marginBottom: 8 }}>
                    {previewLetters}
                  </div>
                  <DactylologyAction>
                    {MAP_COPY.openDactylologyFullScreen}
                  </DactylologyAction>
                </DactylologyCard>
              ) : null}
            </HeroText>
            <HeroIcon $bg={`${categoryColor}18`}>
              <CategoryIcon
                category={poi.category}
                size={56}
                color={mapUi.accent}
                variant="Bold"
              />
            </HeroIcon>
          </HeroRow>
        </HeroCard>

        {poi.subtitle ? <Subtitle>{poi.subtitle}</Subtitle> : null}

        <InfoSection>
          <InfoRow>
            <Location size={24} color={mapUi.accent} variant="Bold" />
            <InfoText>
              <InfoLabel>{MAP_COPY.addressLabel}</InfoLabel>
              <LinkText type="button" onClick={openMapAppsSheet}>
                {poi.address}
              </LinkText>
            </InfoText>
          </InfoRow>

          {poi.hours ? (
            <InfoRow>
              <Clock size={24} color={mapUi.accent} variant="Bold" />
              <InfoText>
                <InfoLabel>{MAP_COPY.hoursLabel}</InfoLabel>
                <InfoValue>{poi.hours}</InfoValue>
              </InfoText>
            </InfoRow>
          ) : null}

          {poi.phone ? (
            <InfoRow>
              <Call size={24} color={mapUi.accent} variant="Bold" />
              <InfoText>
                <InfoLabel>{MAP_COPY.phoneLabel}</InfoLabel>
                <LinkText type="button" onClick={openPhoneSheet}>
                  {poi.phone}
                </LinkText>
              </InfoText>
            </InfoRow>
          ) : null}

          {poi.email ? (
            <InfoRow>
              <Sms size={24} color={mapUi.accent} variant="Bold" />
              <InfoText>
                <InfoLabel>{MAP_COPY.emailLabel}</InfoLabel>
                <LinkText
                  type="button"
                  onClick={() => {
                    window.location.href = `mailto:${poi.email}`;
                  }}
                >
                  {poi.email}
                </LinkText>
              </InfoText>
            </InfoRow>
          ) : null}
        </InfoSection>

        <Actions>
          <Md3Button
            title={MAP_COPY.openInMaps}
            variant="filled"
            fullWidth
            onClick={openMapAppsSheet}
            icon={<Location size={20} variant="Bold" />}
          />
          <Md3Button
            title={MAP_COPY.sharePlace}
            variant="outlined"
            fullWidth
            onClick={handleShare}
            icon={<Share size={20} />}
          />
        </Actions>

        <CategoryFooter>
          <CategoryFooterText $color={categoryColor}>
            {CATEGORY_LABELS[poi.category]}
          </CategoryFooterText>
        </CategoryFooter>
      </Content>

      <MapActionSheet
        open={actionSheet.open}
        title={actionSheet.title}
        description={actionSheet.description}
        options={actionSheet.options}
        onClose={closeActionSheet}
      />
    </MobileLayout>
  );
};

export default MapPoiDetailsPage;
